import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import type { TreeEntity } from '../../../core/models/entity.model';
import { BehaviorSubject, combineLatestWith, filter, map, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ProjectDataActions } from '../../store/project-data/project-data.actions';
import type { HoodPriceData, ProductPriceData, ProductType, ProjectPriceData } from './project-prices.models';
import { buildPricingModelFromProject, pricingPropertyKeys } from './project-prices.helpers';
import {
  selectProjectCurrency,
  selectProjectFeedbackMessages,
  selectProjectWeightUnit
} from '../../store/project-data/project-data.selectors';
import { EntityTypesActions } from '../../../core/store/entity-types/entity-types.actions';
import { MeasurementUnitsActions } from '../../../core/store/measurement-units/measurement-units.actions';
import { ProductMessagesService, ProductMessageStatuses } from '../../services/product-messages.service';
import { selectEntityTypes } from '../../../core/store/entity-types/entity-types.selectors';
import { EntityTypeKey, isHood } from '../../../shared/constants/entity-type-keys';
import { FormControl } from '@angular/forms';
import { createNumericControlValidatorsForProperty } from '../../../entity-editor/utils/validation-helpers';
import { selectUserLanguageCode } from '../../../user-account/store/user-settings/user-settings.selectors';
import { Property } from 'src/app/core/models/property.model';
import { MessagePropertyKey } from 'src/app/core/constants/message-property-keys';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-project-prices',
  templateUrl: './project-prices.component.html',
  styleUrls: ['./project-prices.component.scss'],
  standalone: false
})
export class ProjectPricesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() projectEntity: TreeEntity;

  private projectEntity$ = new BehaviorSubject<TreeEntity>(null);
  protected pricingModel$: Observable<ProjectPriceData>;
  protected hoodMessageStatuses$: Observable<Record<string, ProductMessageStatuses>>;
  protected currencyMeasurementUnit$: Observable<any>;
  protected weightMeasurementUnit$: Observable<any>;
  protected feedbackMessages$: Observable<TreeEntity[]>;

  protected hoodIdToExport: string;

  protected projectBudgetFactorControl: FormControl<number>;
  protected showBudgetPriceControl: FormControl<boolean>;

  private projectBudgetFactorSubscription: Subscription;
  private showBudgetPriceSubscription: Subscription;

  localeCode$: Observable<string>;

  constructor(private store: Store, productMessagesService: ProductMessagesService) {
    this.pricingModel$ = this.projectEntity$.pipe(
      filter(p => !!p),
      map(project => buildPricingModelFromProject(project))
    );

    this.feedbackMessages$ = this.store.select(selectProjectFeedbackMessages);

    this.localeCode$ = this.store.select(selectUserLanguageCode);

    this.hoodMessageStatuses$ = this.projectEntity$.pipe(
      combineLatestWith(this.store.select(selectEntityTypes)),
      map(([project, entityTypes]) => {
        if (!project || !entityTypes) {
          return {};
        }
        const hoods = project.Children.filter(child => isHood(child.EntityTypeKey));

        const result: Record<string, ProductMessageStatuses> = {};

        hoods.forEach(hood => {
          result[hood.Id] = productMessagesService.getProductMessageStatuses(hood, entityTypes);
        });

        return result;
      })
    );

    this.projectBudgetFactorControl = new FormControl<number>(0, { updateOn: 'blur' });
    this.projectBudgetFactorSubscription = this.projectBudgetFactorControl.valueChanges.subscribe(value => {
      if (!this.projectBudgetFactorControl.invalid) {
        this.projectFactorChanged(value);
      }
    });

    this.showBudgetPriceControl = new FormControl<boolean>(false);
    this.showBudgetPriceSubscription = this.showBudgetPriceControl.valueChanges.subscribe(value => {
      this.showBudgetPriceChange(value);
    });
  }

  ngOnInit(): void {
    this.store.dispatch(EntityTypesActions.entity_types_required());
    this.store.dispatch(MeasurementUnitsActions.measurement_units_needed());
    this.currencyMeasurementUnit$ = this.store.select(selectProjectCurrency);
    this.weightMeasurementUnit$ = this.store.select(selectProjectWeightUnit);
  }

  ngOnDestroy(): void {
    this.projectBudgetFactorSubscription?.unsubscribe();
    this.showBudgetPriceSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectEntity'] && changes['projectEntity'].currentValue) {
      this.projectEntity$.next(this.projectEntity);
      this.projectBudgetFactorControl.setValue(this.projectEntity.Properties['ProjectBudgetFactor'].Value as number, { emitEvent: false });
      this.projectBudgetFactorControl.setValidators(
        createNumericControlValidatorsForProperty(this.projectEntity.Properties['ProjectBudgetFactor'])
      );

      this.showBudgetPriceControl.setValue(this.projectEntity.Properties['ShowBudgetPrice'].Value as boolean, { emitEvent: false });
    }
  }

  protected projectFactorChanged(newValue: number) {
    this.store.dispatch(
      ProjectDataActions.update_project_start({
        entityId: this.projectEntity.Id,
        propertyKey: pricingPropertyKeys.projectBudgetFactor,
        newValue
      })
    );
  }

  protected showBudgetPriceChange(newValue: boolean) {
    this.store.dispatch(
      ProjectDataActions.update_project_start({
        entityId: this.projectEntity.Id,
        propertyKey: pricingPropertyKeys.showBudgetPrice,
        newValue
      })
    );
  }

  protected factorChanged(newValue: number, hoodId: string, product: ProductType): void {
    let propertyKey = '';

    if (product === 'hood') {
      propertyKey = pricingPropertyKeys.hoodBudgetFactor;
    } else if (product === 'uv') {
      propertyKey = pricingPropertyKeys.uvBudgetFactor;
    }

    if (propertyKey) {
      this.store.dispatch(
        ProjectDataActions.update_project_start({
          entityId: hoodId,
          propertyKey,
          newValue,
          updateFullProject: true
        })
      );
    }
  }

  priceChanged(newValue: number, hoodId: string, productType: 'hood' | 'uv' | 'fireExtinguishing') {
    let propertyKey = '';

    if (productType === 'hood') {
      propertyKey = pricingPropertyKeys.hood.budget;
    } else if (productType === 'uv') {
      propertyKey = pricingPropertyKeys.uv.budget;
    } else {
      propertyKey = pricingPropertyKeys.fireExtinguishing.budget;
    }

    if (propertyKey) {
      this.store.dispatch(
        ProjectDataActions.update_project_start({
          entityId: hoodId,
          propertyKey,
          newValue,
          updateFullProject: true
        })
      );
    }
  }

  toggleHoodExportToPyramid(event: MatCheckboxChange, hoodId: string) {
    this.store.dispatch(
      ProjectDataActions.update_project_start({
        entityId: hoodId,
        propertyKey: 'IncludeInExports',
        newValue: event.checked,
        updateFullProject: true
      })
    );
  }

  toggleExportAllToPyramid(event: MatCheckboxChange, pricingModel: ProjectPriceData) {
    const hoodIds = pricingModel.hoods.map(x => x.hoodId);

    this.store.dispatch(
      ProjectDataActions.update_project_entities_start({
        entityIds: hoodIds,
        propertyKey: 'IncludeInExports',
        newValue: event.checked
      })
    );
  }

  allSelectedForExport(pricingModel: ProjectPriceData) {
    return pricingModel.hoods.every(x => x.includeInExports);
  }

  someSelectedForExport(pricingModel: ProjectPriceData) {
    return pricingModel.hoods.some(x => !x.includeInExports) && pricingModel.hoods.some(x => x.includeInExports);
  }

  protected trackHoodPrice(i: number, hood: HoodPriceData) {
    return hood.hoodId;
  }

  protected trackHoodProduct(i: number, product: ProductPriceData) {
    return product.productType;
  }

  protected showPrice(price: Property): boolean {
    if (price.Rules?.VisibleIf) {
      return true;
    }
    return false;
  }

  protected showBudgetPrice(): boolean {
    return this.projectEntity.Properties['ShowBudgetPrice'].Value as boolean;
  }

  protected budgetFactorMessages() {
    const messages = this.projectEntity.Children.filter(
      x => x.EntityTypeKey === EntityTypeKey.Message && x.Properties[MessagePropertyKey.PropertyKey]?.Value === 'ProjectBudgetFactor'
    );

    return messages;
  }
}
